/**
 * `<SurfaceOneApp />` — Surface 1 hosted patient walkthrough.
 *
 * Composes the production `@shn/phg-client-web` `<App />` against
 * `@shn/phg-client-demo-data`'s synthetic seed via the wrapper-
 * injection seam (PR #210 concern #3 disposition; `App.injectedSessionResidentDeps`).
 *
 * **Architectural posture per PR #210 §5.4 coherence test:** Surface 1
 * differs from production only at the composition root. The product
 * surface IS the real PHG client running real substrate APIs against
 * synthetic data — NOT a parallel marketing scaffold; NO "demo mode"
 * code paths inside the PHG client. The wrapper does ONE thing
 * production wrappers don't: it pre-encrypts synthetic records into
 * the `MockProjectionServiceClient` before mounting `<App />`.
 *
 * **3-scene minimum-viable per §9 Risk 2 contraction posture:**
 * Activation (Scene 1) + Aggregation (Scene 2) + Patient Projection
 * (Scene 3). Recovery (Scene 4) + Third-party-app-consent (Scene 5)
 * follow on if Sprint 1+2 capacity permits.
 */

import { App, type AppProps } from '@shn/phg-client-web';
import type { IAuthenticationFlow, AuthState } from '@shn/phg-client-web/auth';
import type { KekId } from '@shn/substrate-types';
import * as React from 'react';

import { NarrativeOverlay } from './narrative-overlay.js';
import { preMountSurfaceOne, type SurfaceOnePreMountResult } from './seed-mock-projection.js';

/**
 * Synthetic auth flow that reports fixed `session-active` state — the
 * Surface 1 walkthrough's Aggregation + Patient Projection scenes
 * require post-sign-in state, but the demo doesn't ask the viewer
 * (state Medicaid Director) to perform a sign-in flow on their own
 * behalf. The wrapper bypasses sign-in by injecting a session-active
 * `IAuthenticationFlow` whose state matches what production AppShell
 * sees post-real-sign-in.
 *
 * This is the ONE place Surface 1 "fakes" a substrate primitive, and
 * it's bounded: `<AuthenticationView />` is not part of the 3-scene
 * walkthrough (the demo navigates Activation → Aggregation →
 * Projection, skipping the auth view that this fake sits behind).
 * v1.5+ amendment territory: drive a real `AuthenticationFlow`
 * stepped through to session-active programmatically + record the
 * sign-in scene as Scene 1.5.
 */
function makeFakeSessionActiveFlow(handle: {
  readonly kekId: KekId;
  readonly key: CryptoKey;
}): IAuthenticationFlow {
  const state: AuthState = { kind: 'session-active', handle };
  return {
    getState: () => state,
    subscribe: (observer) => {
      observer(state);
      return () => {};
    },
    beginSignIn: () => {},
    submitKnowledgeFactor: () => {},
    submitPossessionFactor: async () => {},
    signOut: () => {},
    restart: () => {},
  };
}

interface PreparedAppProps {
  readonly authenticationFlow: IAuthenticationFlow;
  readonly injectedSessionResidentDeps: SurfaceOnePreMountResult['sessionDeps'];
}

export function SurfaceOneApp(): React.ReactElement {
  const [prepared, setPrepared] = React.useState<PreparedAppProps | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await preMountSurfaceOne();
        if (cancelled) return;
        const session = result.sessionDeps.activeSession;
        const handle = session.requireKek();
        const flow = makeFakeSessionActiveFlow(handle);
        setPrepared({
          authenticationFlow: flow,
          injectedSessionResidentDeps: result.sessionDeps,
        });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error !== null) {
    return (
      <div role="alert" style={{ padding: '1rem', color: '#a00' }}>
        <h2>Walkthrough failed to load</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (prepared === null) {
    return (
      <div role="status" aria-busy="true" style={{ padding: '1rem' }}>
        <p>Preparing the walkthrough...</p>
      </div>
    );
  }

  const appProps: AppProps = {
    initialView: 'activation',
    authenticationFlow: prepared.authenticationFlow,
    injectedSessionResidentDeps: prepared.injectedSessionResidentDeps,
  };

  return (
    <>
      <NarrativeOverlay />
      <App {...appProps} />
    </>
  );
}
